import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FanProfileEditor from '../fan/FanProfileEditor';
import { useAuth } from '../../context/AuthContext';
import { useFanProfileData } from '../../hooks/useFanProfileData';
import supabase from '../../supabase/supabase';

const {
  FiUser, FiLogOut, FiEdit2, FiSliders, FiChevronDown,
  FiMapPin, FiMail, FiBox, FiHeart, FiZap, FiShield, FiTag,
} = FiIcons;

const ROLE_META = {
  platform_admin: { label: 'Platform Admin', color: 'text-red-400 border-red-500/20'    },
  label_admin:    { label: 'Label Admin',     color: 'text-purple-400 border-purple-500/20' },
  artist:         { label: 'Artist',          color: 'text-brand-pink border-brand-pink/20' },
  fan:            { label: 'Fan',             color: 'text-sky-400 border-sky-400/20'    },
};

const ProfileDropdown = () => {
  const { user, profile, refetchProfile } = useAuth();
  const navigate = useNavigate();
  const [open,       setOpen]       = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const ref = useRef(null);

  const { stats } = useFanProfileData(user?.id);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    setOpen(false);
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleSaved = () => { refetchProfile?.(); setShowEditor(false); };

  if (!user) return null;

  const displayName = profile?.display_name || user.email?.split('@')[0] || 'User';
  const role        = profile?.role || 'fan';
  const { label: roleLabel, color: roleColor } = ROLE_META[role] || ROLE_META.fan;
  const genres      = profile?.favorite_genres || [];

  /* Build menu based on role */
  const menuItems = [
    { icon: FiUser,  label: 'My Profile',  action: () => { setOpen(false); navigate('/profile'); } },
    { icon: FiEdit2, label: 'Edit Profile', action: () => { setOpen(false); setShowEditor(true); } },
  ];

  if (role === 'platform_admin') {
    menuItems.push({ icon: FiShield,  label: 'Admin Panel',    action: () => { setOpen(false); navigate('/admin'); } });
    menuItems.push({ icon: FiSliders, label: 'Creator Studio', action: () => { setOpen(false); navigate('/dashboard'); } });
  } else if (role === 'label_admin') {
    menuItems.push({ icon: FiTag,     label: 'Label HQ',       action: () => { setOpen(false); navigate('/label-admin'); } });
    menuItems.push({ icon: FiSliders, label: 'Creator Studio', action: () => { setOpen(false); navigate('/dashboard'); } });
  } else if (role === 'artist') {
    menuItems.push({ icon: FiSliders, label: 'Creator Studio', action: () => { setOpen(false); navigate('/dashboard'); } });
  } else {
    menuItems.push({ icon: FiBox, label: 'My Library', action: () => { setOpen(false); navigate('/profile'); } });
  }

  return (
    <>
      <div ref={ref} className="relative">
        <button
          onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-2xl border transition-all ${
            open ? 'border-brand-pink/30 bg-white/5' : 'border-white/8 bg-white/3 hover:border-brand-pink/20 hover:bg-white/5'
          }`}
        >
          <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-brand-pink/20 bg-gradient-to-br from-brand-pink/20 to-brand-coral/10">
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiUser} className="text-[10px] text-brand-pink" /></div>
            }
          </div>
          <span className="text-xs font-black uppercase tracking-widest text-zinc-300 hidden sm:block max-w-[110px] truncate">{displayName}</span>
          <SafeIcon icon={FiChevronDown} className={`text-xs text-zinc-500 transition-transform duration-200 hidden sm:block ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute right-0 top-[calc(100%+10px)] w-72 z-[200]">
            <div className="rounded-[1.5rem] overflow-hidden border border-white/8 shadow-2xl" style={{ background: 'rgba(10,10,12,0.97)', backdropFilter: 'blur(24px)' }}>

              {/* Header */}
              <div className="p-5 border-b border-white/5 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 border border-brand-pink/20 bg-gradient-to-br from-brand-pink/20 to-brand-coral/10">
                    {profile?.avatar_url
                      ? <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiUser} className="text-2xl text-brand-pink/50" /></div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-black text-white uppercase tracking-tight truncate">{displayName}</p>
                    {profile?.username && <p className="text-[10px] font-black text-zinc-600 tracking-widest">@{profile.username}</p>}
                    <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest ${roleColor}`}>
                      {roleLabel}
                    </span>
                  </div>
                </div>

                {profile?.bio && <p className="text-[11px] font-bold text-zinc-500 leading-relaxed line-clamp-2">{profile.bio}</p>}

                <div className="flex flex-wrap gap-3">
                  {profile?.location && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-zinc-600">
                      <SafeIcon icon={FiMapPin} className="text-[9px] text-brand-pink" /> {profile.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-[10px] font-bold text-zinc-600">
                    <SafeIcon icon={FiMail} className="text-[9px]" /> {user.email}
                  </span>
                </div>

                {genres.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {genres.slice(0, 4).map(g => (
                      <span key={g} className="px-2 py-0.5 rounded-lg bg-white/5 border border-white/8 text-[9px] font-black uppercase tracking-widest text-zinc-500">{g}</span>
                    ))}
                    {genres.length > 4 && (
                      <span className="px-2 py-0.5 rounded-lg bg-white/5 border border-white/8 text-[9px] font-black uppercase tracking-widest text-zinc-700">+{genres.length - 4}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 divide-x divide-white/5 border-b border-white/5">
                {[
                  { label: 'Library',   value: stats.library,   icon: FiBox   },
                  { label: 'Following', value: stats.following, icon: FiHeart },
                  { label: 'Unlocked',  value: stats.unlocked,  icon: FiZap   },
                ].map(({ label, value, icon }) => (
                  <div key={label} className="py-3 flex flex-col items-center gap-1">
                    <SafeIcon icon={icon} className="text-xs text-zinc-600" />
                    <p className="text-sm font-black text-white">{value}</p>
                    <p className="text-[9px] font-black uppercase tracking-widest text-zinc-700">{label}</p>
                  </div>
                ))}
              </div>

              {/* Menu items */}
              <div className="p-2 space-y-0.5">
                {menuItems.map(({ icon, label, action }) => (
                  <button key={label} onClick={action}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-white/5 transition-all text-left group">
                    <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center flex-shrink-0 group-hover:border-brand-pink/20 group-hover:bg-brand-pink/5 transition-all">
                      <SafeIcon icon={icon} className="text-xs group-hover:text-brand-pink transition-colors" />
                    </div>
                    {label}
                  </button>
                ))}
              </div>

              {/* Sign out */}
              <div className="p-2 pt-0 border-t border-white/5 mt-1">
                <button onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-600 hover:text-red-400 hover:bg-red-500/5 transition-all group">
                  <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center flex-shrink-0 group-hover:border-red-500/20 group-hover:bg-red-500/5 transition-all">
                    <SafeIcon icon={FiLogOut} className="text-xs group-hover:text-red-400 transition-colors" />
                  </div>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showEditor && (
        <FanProfileEditor profile={profile} userId={user?.id} onClose={() => setShowEditor(false)} onSaved={handleSaved} />
      )}
    </>
  );
};

export default ProfileDropdown;